import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Compass, Sparkles, MapPin, PlaneTakeoff } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [refCode, setRefCode] = useState(() => new URLSearchParams(window.location.search).get("ref") || "");
  const [busy, setBusy] = useState(false);

  // If arriving via ?ref=CODE, jump to signup by default
  React.useEffect(() => { if (refCode) setMode("signup"); }, [refCode]);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await register(email, password, name, refCode.trim() || undefined);
        toast.success(refCode ? "Account created — Pro month unlocked" : "Account created");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* LEFT: Editorial hero */}
        <div className="relative overflow-hidden bg-stone-900 text-white">
          <img
            src="https://images.unsplash.com/photo-1534445867742-43195f401b6c?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-stone-950/70 via-stone-900/40 to-transparent" />
          <div className="relative z-10 h-full p-8 sm:p-12 flex flex-col">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#C85A32] flex items-center justify-center">
                <Compass size={20} />
              </div>
              <span className="font-serif-display text-2xl font-bold">WanderLoom</span>
            </div>
            <div className="flex-1 flex flex-col justify-end">
              <div className="font-mono-code text-xs tracking-[0.3em] uppercase text-amber-300 mb-4">Issue №01 · An Editorial Travel Journal</div>
              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-lg">
                Plan trips the way great <em className="text-amber-300">stories</em> are written.
              </h1>
              <p className="mt-6 text-stone-200 max-w-md">
                Weave day-by-day itineraries, log flights and stays, generate plans with AI, and share a beautiful public link with fellow travelers.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 text-sm">
                <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20"><PlaneTakeoff size={14} className="text-amber-300"/> Flights log</span>
                <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20"><MapPin size={14} className="text-amber-300"/> Route map</span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Auth */}
        <div className="flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <div className="font-mono-code text-xs uppercase tracking-[0.25em] text-amber-700 mb-2">Begin your journey</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-stone-900">Welcome to WanderLoom</h2>
              <p className="text-stone-600 mt-2">Sign in or create an account to start planning.</p>
            </div>

            <Button
              onClick={handleGoogle}
              variant="outline"
              className="w-full h-11 rounded-xl border-stone-300 hover:bg-stone-50"
              data-testid="auth-google-button"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2">
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.1 17.7 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.6 24.5c0-1.5-.1-3-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.4 5.7c4.3-4 6.8-9.9 6.8-17.4z"/>
                <path fill="#FBBC05" d="M10.5 28.6c-.5-1.5-.8-3.1-.8-4.6s.3-3.1.8-4.6l-7.9-6.1C.9 16.3 0 20 0 24s.9 7.7 2.6 10.7l7.9-6.1z"/>
                <path fill="#34A853" d="M24 48c6.2 0 11.5-2.1 15.3-5.6l-7.4-5.7c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.6-3.6-14.3-8.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="my-6 flex items-center gap-3 text-xs text-stone-500 font-mono-code uppercase tracking-widest">
              <div className="h-px flex-1 bg-stone-200" /> or <div className="h-px flex-1 bg-stone-200" />
            </div>

            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-2 mb-4 bg-stone-100">
                <TabsTrigger value="signin" data-testid="auth-tab-signin">Sign in</TabsTrigger>
                <TabsTrigger value="signup" data-testid="auth-tab-signup">Create account</TabsTrigger>
              </TabsList>

              <form onSubmit={submit} className="space-y-4">
                {mode === "signup" && (
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" data-testid="auth-name-input" value={name} onChange={(e)=>setName(e.target.value)} required className="h-11 mt-1 bg-white" placeholder="Amelia Traveler" />
                  </div>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" data-testid="auth-email-input" value={email} onChange={(e)=>setEmail(e.target.value)} required className="h-11 mt-1 bg-white" placeholder="you@example.com" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" data-testid="auth-password-input" value={password} onChange={(e)=>setPassword(e.target.value)} required minLength={6} className="h-11 mt-1 bg-white" placeholder="••••••••" />
                </div>
                {mode === "signup" && (
                  <div>
                    <Label htmlFor="ref">Referral code <span className="text-stone-400 font-normal">(optional)</span></Label>
                    <Input id="ref" data-testid="auth-referral-input" value={refCode} onChange={(e)=>setRefCode(e.target.value.toUpperCase())} className="h-11 mt-1 bg-white font-mono-code" placeholder="WL-XXXXXX" />
                    {refCode && <div className="text-xs text-amber-700 mt-1">You'll start with 1 free month of Pro.</div>}
                  </div>
                )}
                <Button type="submit" disabled={busy} className="w-full h-11 bg-[#C85A32] hover:bg-[#B04B26] text-white rounded-xl" data-testid="auth-submit-button">
                  {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
                </Button>
                <div className="text-xs text-center text-stone-500 mt-3 font-mono-code">
                  Try demo: <span className="text-stone-700">demo@wanderloom.app</span> / <span className="text-stone-700">Demo1234!</span>
                </div>
              </form>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
